import type { ReactNode } from 'react';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import { brand } from '@/shared/config/brand';
import { legalConfig } from '@/shared/config/legal';
import { SeoHead } from '@/shared/components/SeoHead';

interface LegalPageLayoutProps {
  title: string;
  metaTitle: string;
  metaDescription: string;
  children: ReactNode;
}

export function LegalPageLayout({
  title,
  metaTitle,
  metaDescription,
  children,
}: LegalPageLayoutProps) {
  return (
    <Box sx={{ width: '100%', bgcolor: 'background.default', py: { xs: 4, md: 8 } }}>
      <SeoHead title={metaTitle} description={metaDescription} />
      <Container maxWidth="md" sx={{ px: { xs: 2.5, sm: 3 } }}>
        <Typography
          component="h1"
          sx={{
            fontFamily: '"Playfair Display", Georgia, serif',
            fontSize: { xs: '1.75rem', sm: '2.25rem', md: '2.75rem' },
            fontWeight: 700,
            letterSpacing: '-0.02em',
            mb: 1,
            color: 'text.primary',
          }}
        >
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {brand.uiName} · {legalConfig.websiteUrl.replace(/^https?:\/\//, '')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Effective date: {legalConfig.effectiveDate}
        </Typography>
        <Divider sx={{ mb: { xs: 3, md: 4 } }} />
        <Box
          sx={{
            '& h2': {
              fontSize: { xs: '1.125rem', md: '1.25rem' },
              fontWeight: 700,
              mt: { xs: 3.5, md: 4.5 },
              mb: 1.25,
              color: 'text.primary',
            },
            '& h3': {
              fontSize: '1rem',
              fontWeight: 650,
              mt: 2.5,
              mb: 1,
              color: 'text.primary',
            },
            '& p, & li': {
              fontSize: { xs: '0.9375rem', md: '1rem' },
              lineHeight: 1.75,
              color: 'text.secondary',
            },
            '& p': { mb: 1.5 },
            '& ul': { pl: 2.5, mb: 2, mt: 0 },
            '& li': { mb: 0.75 },
            '& a': {
              color: 'primary.main',
              fontWeight: 600,
              textUnderlineOffset: 3,
            },
          }}
        >
          {children}
        </Box>
      </Container>
    </Box>
  );
}

export function LegalSection({
  id,
  title,
  children,
}: {
  id?: string;
  title: string;
  children: ReactNode;
}) {
  return (
    <Box component="section" id={id} sx={{ scrollMarginTop: 96 }}>
      <Typography component="h2">{title}</Typography>
      {children}
    </Box>
  );
}
